import { motion } from "framer-motion";
import { ArrowUpRight, Briefcase, Music2, Sparkles, Trees, Users, Utensils } from "lucide-react";
import { ASSETS, whatsappHref } from "../../data/content";
import { FadeUp, MaskLineInView } from "./Reveal";

const EVENT_TYPES = [
  {
    title: "Weddings & Milestones",
    body: "Intimate ceremonies under mango canopies, riverside sundowners and a house full of family that finally slows down together.",
  },
  {
    title: "Birthdays & Anniversaries",
    body: "Long dinner tables on the lawn, cake by the water, and a whole property to fill with music, laughter and lanterns.",
  },
  {
    title: "Private Gatherings",
    body: "A place to bring the people you love, close the gate behind you, and be gloriously undisturbed for a weekend.",
  },
  {
    title: "Corporate Retreats",
    body: "Offsites, planning weekends and strategy sessions that actually breathe — expansive grounds with quiet corners built for focus.",
  },
];

const PERKS = [
  { icon: Users, label: "Sleeps groups of up to 30" },
  { icon: Trees, label: "Sprawling grounds & river frontage" },
  { icon: Utensils, label: "All meals crafted in-house" },
  { icon: Briefcase, label: "Quiet corners for focus & offsites" },
];

export const Events = () => {
  return (
    <section
      id="events"
      data-testid="events-section"
      className="relative bg-[#fbf8f0] py-12 lg:py-16 overflow-hidden"
    >
      <img
        src={ASSETS.monogramGold}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="pointer-events-none absolute -right-24 top-16 w-[380px] opacity-[0.06]"
      />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
          {/* Feature image */}
          <FadeUp className="lg:col-span-6 lg:h-full">
            <div className="relative h-full">
              <div className="absolute -inset-3 sm:-inset-4 border border-[#07412b]/25 rounded-[36px] -translate-x-3 -translate-y-3" />
              <motion.div
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-[32px] shadow-[0_30px_60px_-25px_rgba(7,65,43,0.4)] h-full min-h-[440px]"
              >
                <img
                  src={ASSETS.eventSinger}
                  alt="A live acoustic performance at The Brook"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-[center_30%]"
                />
              </motion.div>
            </div>
          </FadeUp>

          {/* Copy + cards */}
          <div className="lg:col-span-6">
            <FadeUp>
              <span className="inline-flex items-center gap-2 font-body text-[#c56928] text-[13px] tracking-[0.3em] uppercase">
                <Sparkles size={14} /> Events & Retreats
              </span>
            </FadeUp>
            <h2 className="font-display text-[#07412b] text-[52px] sm:text-7xl lg:text-[110px] leading-[1] mt-2">
              <MaskLineInView>Celebrate here.</MaskLineInView>
              <MaskLineInView delay={0.08}>Remember it forever.</MaskLineInView>
            </h2>
            <FadeUp delay={0.15}>
              <p className="font-body font-light text-[#07412b]/75 text-base sm:text-lg mt-4 max-w-lg leading-relaxed">
                A private property by the river is a rare kind of venue — quiet enough to
                hear a promise, generous enough to hold everyone you love, and
                unhurried enough to think clearly together. We host a handful
                of events and retreats each season, and curate every detail
                with you.
              </p>
            </FadeUp>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-9">
              {EVENT_TYPES.map((e, i) => (
                <motion.div
                  key={e.title}
                  data-testid={`event-card-${i + 1}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-8%" }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 }}
                  className="group relative rounded-2xl border border-[#07412b]/12 bg-white/60 p-5 sm:p-6 hover:border-[#ddb128] transition-colors duration-500"
                >
                  <h3 className="font-display text-[#07412b] text-xl sm:text-2xl leading-tight">
                    {e.title}
                  </h3>
                  <p className="font-body font-light text-[#07412b]/70 text-sm leading-relaxed mt-2">
                    {e.body}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="mt-9 grid grid-cols-2 gap-3">
              {PERKS.map((p, i) => (
                <motion.div
                  key={p.label}
                  data-testid={`event-perk-${i + 1}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="flex items-start gap-3 rounded-xl bg-[#07412b]/[0.04] p-3.5"
                >
                  <p.icon size={18} className="text-[#c56928] mt-0.5 shrink-0" />
                  <span className="font-body font-light text-[#07412b] text-[13px] sm:text-sm leading-snug">
                    {p.label}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* Live Music add-on */}
            <motion.a
              href={whatsappHref(
                "Hi! I'd like to enquire about the live acoustic music add-on for my stay/event at The Brook."
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="live-music-addon"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -2 }}
              className="group relative mt-9 flex items-center gap-4 rounded-2xl border border-[#ddb128]/50 bg-[#07412b] p-4 sm:p-5 overflow-hidden hover:border-[#ddb128] transition-colors"
            >
              <span className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-full bg-[#ddb128] text-[#07412b] flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                <Music2 size={22} strokeWidth={1.7} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-body text-[#ddb128] text-[10px] tracking-[0.24em] uppercase">
                    Add-on
                  </span>
                  <span className="h-1 w-1 rounded-full bg-[#ddb128]/50" aria-hidden="true" />
                  <span className="font-body text-[#f7f3e9]/60 text-[10px] tracking-[0.16em] uppercase">
                    Pricing on enquiry
                  </span>
                </div>
                <p className="font-display text-[#f7f3e9] text-xl sm:text-2xl leading-tight mt-1">
                  Live Acoustic Music
                </p>
                <p className="font-body font-light text-[#f7f3e9]/70 text-[13px] sm:text-sm leading-relaxed mt-1">
                  Book our in-house musician for the evening — quiet dinners,
                  sundowners and celebrations, in your own private soundtrack.
                </p>
              </div>
              <ArrowUpRight
                size={20}
                className="text-[#ddb128] shrink-0 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </motion.a>

            <FadeUp delay={0.15}>
              <motion.a
                href={whatsappHref(
                  "Hi! I'd like to enquire about hosting an event or retreat at The Brook."
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="events-enquire-btn"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#ddb128] text-[#07412b] px-8 py-4 text-[12px] tracking-[0.18em] uppercase font-semibold hover:bg-[#f0c545] transition-colors duration-300"
              >
                Plan an Event or Retreat
                <ArrowUpRight size={16} />
              </motion.a>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};
